/**
 * useLocalDeviceStore — Surface Layer (/01)
 *
 * Offline UI cache backed by localStorage today; SwiftData via the
 * WebKit bridge tomorrow. The shape is intentionally minimal: a typed
 * JSON value at a stable string key.
 *
 * Maps to: /00/DataGate.schema.json -> LocalDeviceStore.
 */

import { useCallback, useEffect, useState } from 'react';

const NAMESPACE = 'surface.local-device-store.v1';

function buildKey(scopeKey: string): string {
  return `${NAMESPACE}::${scopeKey}`;
}

function readFromStorage<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(buildKey(key));
    if (raw === null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeToStorage<T>(key: string, value: T): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(buildKey(key), JSON.stringify(value));
  } catch (err) {
    // Quota exceeded or storage disabled. Swallow — this is a cache,
    // not the source of truth.
    // eslint-disable-next-line no-console
    console.warn('[local-device-store] write failed', err);
  }
}

/**
 * Persisted typed value. Same ergonomics as useState plus initial
 * read from storage.
 */
export function useLocalDeviceStore<T>(key: string, fallback: T): [
  T,
  (next: T | ((prev: T) => T)) => void,
] {
  const [value, setValue] = useState<T>(() => readFromStorage(key, fallback));

  useEffect(() => {
    writeToStorage(key, value);
  }, [key, value]);

  const set = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValue((prev) => {
        const computed = typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
        return computed;
      });
    },
    []
  );

  return [value, set];
}
